import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSearch, Loader2 } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../redux/store';
import { userSelectOneObjectByQuery, DBChatThunks } from '../../redux/thunks/DBServiceThunks';
import { JurisdictionCode, DEFAULT_JURISDICTION } from '../../common/jurisdictions';
import JurisdictionSelect from '../common/JurisdictionSelect';
import KnowledgebaseSelector from './KnowledgebaseSelector';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import {
  setJurisdiction,
  setQuery,
  setIsSubmitting,
  resetDocumentSearch
} from '../../redux/slices/documentSearchSlice';

interface DocumentSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
  onSearchSubmit: (data: {
    query: string;
    selectedKnowledgebaseIds: string[];
    jurisdiction: JurisdictionCode;
    chatId: string;
  }) => void;
}

/**
 * DocumentSearchModal Component
 * Modal for creating a new document search with KB selection and jurisdiction override
 */
const DocumentSearchModal: React.FC<DocumentSearchModalProps> = ({
  isOpen,
  onClose,
  caseId,
  onSearchSubmit
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const currentUser = useAppSelector(state => userSelectOneObjectByQuery(state.DBUserReducer, {}));
  const currentCase = useAppSelector(state => state.DBCaseReducer.entities?.[caseId] as any);

  const defaultJurisdiction: JurisdictionCode = (currentCase?.jurisdiction || currentUser?.jurisdiction || DEFAULT_JURISDICTION) as JurisdictionCode;

  // Get state from Redux store
  const state = useAppSelector(state => state.documentSearch);

  // Reset state when modal unmounts/closes
  useEffect(() => {
    if (!isOpen) {
      dispatch(resetDocumentSearch({ jurisdiction: defaultJurisdiction }));
    }
  }, [isOpen, defaultJurisdiction, dispatch]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!state.query.trim()) {
      return;
    }

    if (state.selectedKnowledgebaseIds.length === 0) {
      return;
    }

    dispatch(setIsSubmitting(true));

    try {
      // Create a new chat for this document search using thunk
      const resultAction = await dispatch(DBChatThunks.create({
        title: `Document Search: ${state.query.substring(0, 50)}${state.query.length > 50 ? '...' : ''}`,
        caseId: caseId,
        type: 'documentSearch' // Mark this as a document search chat
      }));

      // Check if creation was successful
      if (DBChatThunks.create.fulfilled.match(resultAction)) {
        const newChat = resultAction.payload;

        // Submit the search
        onSearchSubmit({
          query: state.query.trim(),
          selectedKnowledgebaseIds: state.selectedKnowledgebaseIds,
          jurisdiction: state.jurisdiction,
          chatId: newChat._id
        });

        // Close modal (state will be reset by useEffect)
        onClose();
      }
      // TODO: Show error to user if creation failed
    } catch (error: any) {
      // TODO: Show error to user
    } finally {
      dispatch(setIsSubmitting(false));
    }
  };

  const handleClose = () => {
    if (!state.isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSearch className="h-5 w-5" />
            {t('components.documentSearch.title', 'Search Legal Documents')}
          </DialogTitle>
          <DialogDescription>
            {t('components.documentSearch.description', 'Select knowledgebases and enter your search query to find relevant legal documents')}
          </DialogDescription>
        </DialogHeader>

        {/*
          NOTE: intentionally a <div>, not a <form>. Inside a <form>, Radix
          Checkbox renders a hidden form-sync input whose ref updates on every
          toggle; with many KB checkboxes toggling at once (e.g. "Select All")
          this triggers an infinite Presence ref-detach loop (React #185).
          Submit is handled via the button's onClick instead.
        */}
        <div className="space-y-6">
          {/* Jurisdiction selector */}
          <div>
            <Label className="text-sm font-medium mb-2 block">
              {t('components.documentSearch.jurisdiction', 'Jurisdiction')}
            </Label>
            <JurisdictionSelect
              value={state.jurisdiction}
              onChange={(newJurisdiction) => dispatch(setJurisdiction(newJurisdiction))}
              label={t('components.documentSearch.jurisdiction', 'Jurisdiction')}
              size="medium"
              fullWidth
            />
          </div>

          {/* Knowledgebase selector */}
          <div>
            <Label className="text-sm font-medium mb-2 block">
              {t('components.documentSearch.selectKnowledgebases', 'Select Knowledgebases')}
            </Label>
            <KnowledgebaseSelector
              jurisdiction={state.jurisdiction}
            />
          </div>

          {/* Search query textarea */}
          <div>
            <Label htmlFor="search-query" className="text-sm font-medium mb-2 block">
              {t('components.documentSearch.query', 'Search Query')}
            </Label>
            <Textarea
              id="search-query"
              value={state.query}
              onChange={(e) => dispatch(setQuery(e.target.value))}
              placeholder={t('components.documentSearch.searchPlaceholder', 'Enter your search query...')}
              rows={4}
              className="resize-none"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={state.isSubmitting}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <div className="w-1"></div>
            <Button
              type="button"
              onClick={() => handleSubmit()}
              disabled={!state.query.trim() || state.selectedKnowledgebaseIds.length === 0 || state.isSubmitting}
            >
              {state.isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('components.documentSearch.searching', 'Searching...')}
                </>
              ) : (
                <>
                  <FileSearch className="mr-2 h-4 w-4" />
                  {t('components.documentSearch.searchButton', 'Search Documents')}
                </>
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentSearchModal;

